import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/esm/Add';
import CertifyIcon from '@mui/icons-material/esm/AssignmentTurnedIn';
import RejectIcon from '@mui/icons-material/esm/Block';
import DocumentIcon from '@mui/icons-material/esm/Description';
import DownloadIcon from '@mui/icons-material/esm/FileDownload';
import PacketIcon from '@mui/icons-material/esm/Inventory';
import RefreshIcon from '@mui/icons-material/esm/Refresh';
import { PageHeader } from '../../components/layout';
import { KPICard } from '../../components/common';
import qsService, {
  ClaimableCertifiedBqLine,
  DrawdownPreview,
  QsCertificateLine,
  RecommendationForPaymentPreview,
} from '../../services/api/qsService';
import wbsService, { WBSTreeNode } from '../../services/api/wbsService';
import { RootState } from '../../store';

interface CertificateFormState {
  lineCode: string;
  description: string;
  quantity: string;
  unitOfMeasure: string;
  certifiedValue: string;
  retentionPercent: string;
  notes: string;
}

const emptyForm: CertificateFormState = {
  lineCode: '',
  description: '',
  quantity: '1',
  unitOfMeasure: 'item',
  certifiedValue: '',
  retentionPercent: '5',
  notes: '',
};

const readinessFields = [
  { key: 'certifiedValue', label: 'Certified' },
  { key: 'fieldEvidence', label: 'Evidence' },
  { key: 'documents', label: 'Docs' },
  { key: 'permits', label: 'Permits' },
  { key: 'insurance', label: 'Insurance' },
];

const formatMoney = (value: number): string => `$${Number(value || 0).toLocaleString()}`;

const flattenWbs = (nodes: WBSTreeNode[]): WBSTreeNode[] =>
  nodes.flatMap((node) => [node, ...flattenWbs(node.children || [])]);

export default function QsWorkspacePage() {
  const { activeProjectId } = useSelector((state: RootState) => state.projects);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [wbsNodes, setWbsNodes] = useState<WBSTreeNode[]>([]);
  const [selectedWbsId, setSelectedWbsId] = useState<string>('');
  const [linkedTasks, setLinkedTasks] = useState<any[]>([]);
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const [certificateLines, setCertificateLines] = useState<QsCertificateLine[]>([]);
  const [claimableLines, setClaimableLines] = useState<ClaimableCertifiedBqLine[]>([]);
  const [claimableDetail, setClaimableDetail] = useState<ClaimableCertifiedBqLine | null>(null);
  const [recommendationPreview, setRecommendationPreview] = useState<RecommendationForPaymentPreview | null>(null);
  const [certificateNumber, setCertificateNumber] = useState<number>(1);
  const [previousPayments, setPreviousPayments] = useState<number>(0);
  const [drawdownPreview, setDrawdownPreview] = useState<DrawdownPreview | null>(null);
  const [drawdownReference, setDrawdownReference] = useState('DD-001');
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [rejectDialogLine, setRejectDialogLine] = useState<QsCertificateLine | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [certifyDialogLine, setCertifyDialogLine] = useState<QsCertificateLine | null>(null);
  const [certifyPercent, setCertifyPercent] = useState<number>(100);
  const [form, setForm] = useState<CertificateFormState>(emptyForm);

  const workPackages = useMemo(
    () => flattenWbs(wbsNodes).filter((node) => node.type === 'WORK_PACKAGE' || (node.children || []).length === 0),
    [wbsNodes]
  );

  const selectedWbs = useMemo(
    () => workPackages.find((node) => String(node.id) === selectedWbsId),
    [selectedWbsId, workPackages]
  );

  const selectedLine = certificateLines.find((line) => line.wbsId && String(line.wbsId) === selectedWbsId);

  const loadWorkspace = async () => {
    if (!activeProjectId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const [tree, lines, claimable, recommendation, preview] = await Promise.all([
        wbsService.getProjectTree(activeProjectId),
        qsService.getCertificateLines(activeProjectId),
        qsService.getClaimableCertifiedBqLines(activeProjectId),
        qsService.getRecommendationForPaymentPreview(activeProjectId, { certificateNumber, previousPayments }),
        qsService.getDrawdownPreview(activeProjectId, { groupBy: 'wbs', drawdownReference }),
      ]);

      const safeTree = Array.isArray(tree) ? tree : [];
      setWbsNodes(safeTree);
      setCertificateLines(Array.isArray(lines) ? lines : []);
      setClaimableLines(Array.isArray(claimable) ? claimable : []);
      setRecommendationPreview(recommendation);
      setDrawdownPreview(preview);

      if (!selectedWbsId && safeTree.length > 0) {
        const firstWorkPackage = flattenWbs(safeTree).find((node) => node.type === 'WORK_PACKAGE' || (node.children || []).length === 0);
        if (firstWorkPackage) {
          setSelectedWbsId(String(firstWorkPackage.id));
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load QS workspace');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorkspace();
  }, [activeProjectId]);

  useEffect(() => {
    const loadTasks = async () => {
      if (!selectedWbsId) {
        setLinkedTasks([]);
        return;
      }

      try {
        const tasks = await wbsService.getLinkedTasks(selectedWbsId);
        const safeTasks = Array.isArray(tasks) ? tasks : [];
        setLinkedTasks(safeTasks);
        setSelectedTaskId(safeTasks[0]?.id || '');
      } catch {
        setLinkedTasks([]);
      }
    };

    loadTasks();
  }, [selectedWbsId]);

  const handleCreateLine = async () => {
    if (!activeProjectId || !selectedWbsId) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await qsService.createCertificateLine({
        projectId: activeProjectId,
        wbsId: Number(selectedWbsId),
        taskId: selectedTaskId || undefined,
        lineCode: form.lineCode || `QS-${Date.now()}`,
        description: form.description,
        quantity: Number(form.quantity || 0),
        unitOfMeasure: form.unitOfMeasure || undefined,
        certifiedValue: Number(form.certifiedValue || 0),
        retentionPercent: Number(form.retentionPercent || 0),
        notes: form.notes || undefined,
      });

      setForm(emptyForm);
      setSuccess('Certificate line added.');
      await loadWorkspace();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create certificate line');
    } finally {
      setSaving(false);
    }
  };

  const openCertifyDialog = (line: QsCertificateLine) => {
    setCertifyDialogLine(line);
    // Resume from current cumulative percent for partial top-up. Default to
    // 100 for new lines (status=pending, percentCertified=0/undefined).
    const cumulative = Number(line.percentCertified) || 0;
    setCertifyPercent(cumulative > 0 && cumulative < 100 ? Math.min(100, cumulative + 10) : 100);
  };

  const handleCertify = async () => {
    if (!certifyDialogLine) return;
    const percent = Math.round(certifyPercent);
    if (!Number.isFinite(percent) || percent <= 0 || percent > 100) {
      setError('Percent certified must be between 1 and 100');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await qsService.certifyCertificateLine(certifyDialogLine.id, percent);
      setSuccess(percent === 100
        ? 'Certificate line certified (100%).'
        : `Certificate line certified at ${percent}%.`);
      setCertifyDialogLine(null);
      await loadWorkspace();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to certify line');
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectDialogLine || !rejectReason.trim()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await qsService.rejectCertificateLine(rejectDialogLine.id, rejectReason.trim());
      setSuccess('Certificate line rejected.');
      setRejectDialogLine(null);
      setRejectReason('');
      await loadWorkspace();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reject line');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenClaimableDetail = async (line: ClaimableCertifiedBqLine) => {
    try {
      setSaving(true);
      setError(null);
      const detail = await qsService.getClaimableCertifiedBqLine(line.id);
      setClaimableDetail(detail);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load claimable line detail');
    } finally {
      setSaving(false);
    }
  };

  const handleCertifyClaimable = async (line: ClaimableCertifiedBqLine) => {
    try {
      setSaving(true);
      setError(null);
      await qsService.certifyClaimableCertifiedBqLine(line.id, {
        percentCertified: line.proposedPercentCertified,
        certifiedValue: line.thisCertificateValue,
      });
      setSuccess('Claimable BQ line certified.');
      setClaimableDetail(null);
      await loadWorkspace();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to certify claimable BQ line');
    } finally {
      setSaving(false);
    }
  };

  const handleRejectClaimable = async (line: ClaimableCertifiedBqLine) => {
    const reason = window.prompt('Reason for rejection');
    if (!reason?.trim()) return;
    try {
      setSaving(true);
      setError(null);
      await qsService.rejectClaimableCertifiedBqLine(line.id, reason.trim());
      setSuccess('Claimable BQ line rejected.');
      setClaimableDetail(null);
      await loadWorkspace();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reject claimable BQ line');
    } finally {
      setSaving(false);
    }
  };

  const handleAddToDrawdown = async (line: QsCertificateLine) => {
    try {
      setSaving(true);
      setError(null);
      await qsService.createDrawdownItem(line.id, drawdownReference);
      setSuccess('Line added to drawdown preview.');
      await loadWorkspace();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add line to drawdown');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPacket = async () => {
    if (!activeProjectId) return;

    try {
      setSaving(true);
      setError(null);
      const blob = await qsService.downloadDrawdownPacketPdf(activeProjectId, drawdownReference);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `drawdown-packet-${drawdownReference || 'unassigned'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to download drawdown packet');
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewPacket = async () => {
    if (!activeProjectId) return;

    try {
      setSaving(true);
      setError(null);
      if (pdfPreviewUrl) {
        window.URL.revokeObjectURL(pdfPreviewUrl);
      }
      const blob = await qsService.downloadDrawdownPacketPdf(activeProjectId, drawdownReference);
      setPdfPreviewUrl(window.URL.createObjectURL(blob));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to preview drawdown packet');
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePacketDocument = async () => {
    if (!activeProjectId) return;

    try {
      setSaving(true);
      setError(null);
      await qsService.createDrawdownPacketDocument(activeProjectId, drawdownReference);
      setSuccess('Drawdown packet document link created.');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create packet document link');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleReadiness = async (
    itemId: string,
    currentChecklist: Record<string, boolean> | undefined,
    key: string,
    checked: boolean
  ) => {
    try {
      setSaving(true);
      setError(null);
      await qsService.updateDrawdownItemReadiness(itemId, {
        ...(currentChecklist || {}),
        [key]: checked,
      });
      await loadWorkspace();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update readiness checklist');
    } finally {
      setSaving(false);
    }
  };

  if (!activeProjectId) {
    return (
      <Box>
        <PageHeader title="QS Workspace" />
        <Alert severity="info">Select a project to enter QS certificate lines.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={56} />
      </Box>
    );
  }

  const recommendationTotals = recommendationPreview?.totals;
  const drawdownGroups = Array.isArray(drawdownPreview?.groups) ? drawdownPreview.groups : [];

  return (
    <Box>
      <PageHeader
        title="QS Workspace"
        breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'QS Workspace' }]}
        actions={
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadWorkspace}>
            Refresh
          </Button>
        }
      />

      <Stack spacing={2} sx={{ mb: 3 }}>
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}
      </Stack>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Certificate Lines" value={certificateLines.length} color="primary" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Certified Value"
            value={formatMoney(certificateLines.reduce((sum, line) => sum + line.certifiedValue, 0))}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Amount Due"
            value={formatMoney(recommendationTotals?.amountDue || 0)}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Claimable Lines"
            value={claimableLines.length}
            color="warning"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: 1 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Claimable Work
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Certified BQ valuation, materials on site, approved variations, and readiness blockers.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  label="Certificate No."
                  type="number"
                  value={certificateNumber}
                  onChange={(event) => setCertificateNumber(Number(event.target.value))}
                  inputProps={{ min: 1 }}
                />
                <TextField
                  size="small"
                  label="Previous payments"
                  type="number"
                  value={previousPayments}
                  onChange={(event) => setPreviousPayments(Number(event.target.value))}
                />
                <Button size="small" variant="outlined" onClick={loadWorkspace} disabled={saving}>
                  Reprice
                </Button>
              </Stack>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>BQ line</TableCell>
                  <TableCell>Element</TableCell>
                  <TableCell align="right">BQ value</TableCell>
                  <TableCell align="right">Supported</TableCell>
                  <TableCell align="right">Previous</TableCell>
                  <TableCell align="right">This cert</TableCell>
                  <TableCell align="right">Materials</TableCell>
                  <TableCell align="right">Variations</TableCell>
                  <TableCell align="right">To complete</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {claimableLines.map((line) => (
                  <TableRow key={line.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{line.bqCode}</Typography>
                      <Typography variant="caption" color="text.secondary">{line.description}</Typography>
                    </TableCell>
                    <TableCell>{line.elementGroup || '-'}{line.subElement ? ` / ${line.subElement}` : ''}</TableCell>
                    <TableCell align="right">{formatMoney(line.certifiedBqValue)}</TableCell>
                    <TableCell align="right">{formatMoney(line.supportedWorkToDateValue)}</TableCell>
                    <TableCell align="right">{formatMoney(line.previouslyCertifiedValue)}</TableCell>
                    <TableCell align="right">{formatMoney(line.thisCertificateValue)}</TableCell>
                    <TableCell align="right">{formatMoney(line.materialsOnSiteValue)}</TableCell>
                    <TableCell align="right">{formatMoney(line.approvedVariationsValue)}</TableCell>
                    <TableCell align="right">{formatMoney(line.toCompleteValue)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={line.readiness?.status || 'blocked'}
                        color={line.readiness?.status === 'ready' ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => handleOpenClaimableDetail(line)} disabled={saving}>
                          Detail
                        </Button>
                        <Button
                          size="small"
                          startIcon={<CertifyIcon />}
                          onClick={() => handleCertifyClaimable(line)}
                          disabled={saving || line.readiness?.status !== 'ready' || line.thisCertificateValue <= 0}
                        >
                          Certify
                        </Button>
                        <Button
                          size="small"
                          startIcon={<RejectIcon />}
                          onClick={() => handleRejectClaimable(line)}
                          disabled={saving}
                        >
                          Reject
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {claimableLines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11}>
                      <Alert severity="info">No certified BQ lines are currently claimable for this project.</Alert>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: 1 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Recommendation for Payment
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2}>
                <KPICard title="Work to Date" value={formatMoney(recommendationTotals?.certifiedWorkToDate || 0)} color="primary" />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <KPICard title="Materials" value={formatMoney(recommendationTotals?.materialsOnSiteValue || 0)} color="info" />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <KPICard title="Variations" value={formatMoney(recommendationTotals?.approvedVariationsValue || 0)} color="secondary" />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <KPICard title="Mobilization" value={formatMoney(recommendationTotals?.mobilizationRecovery || 0)} color="warning" />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <KPICard title="Previous" value={formatMoney(recommendationTotals?.previousPayments || 0)} color="error" />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <KPICard title="Due" value={formatMoney(recommendationTotals?.amountDue || 0)} color="success" />
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ mt: 2 }}>
              Mobilization recovery applies at {recommendationPreview?.terms?.mobilizationRecoveryPercent || 20}% for certificates 1-5. Ongoing certificate retention is 0%.
            </Alert>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 2, borderRadius: 1 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Work Package
            </Typography>
            <Stack spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>WBS</InputLabel>
                <Select
                  value={selectedWbsId}
                  label="WBS"
                  onChange={(event) => setSelectedWbsId(event.target.value)}
                >
                  {workPackages.map((node) => (
                    <MenuItem key={node.id} value={String(node.id)}>
                      {node.code} - {node.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Task</InputLabel>
                <Select
                  value={selectedTaskId}
                  label="Task"
                  onChange={(event) => setSelectedTaskId(event.target.value)}
                >
                  <MenuItem value="">No task link</MenuItem>
                  {linkedTasks.map((task) => (
                    <MenuItem key={task.id} value={task.id}>
                      {task.taskCode || task.code} - {task.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedWbs && (
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Progress
                  </Typography>
                  <Typography variant="h6">{selectedWbs.progress}%</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Budget {formatMoney(selectedWbs.budget)}
                  </Typography>
                </Box>
              )}
              {selectedLine && (
                <Alert severity="info">
                  Latest line for this package: {selectedLine.lineCode} ({selectedLine.status})
                </Alert>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 2, borderRadius: 1 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Certificate Line Entry
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Line Code"
                  value={form.lineCode}
                  onChange={(event) => setForm({ ...form, lineCode: event.target.value })}
                  placeholder="Auto if blank"
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  size="small"
                  label="Description"
                  value={form.description}
                  onChange={(event) => setForm({ ...form, description: event.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Quantity"
                  type="number"
                  value={form.quantity}
                  onChange={(event) => setForm({ ...form, quantity: event.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Unit"
                  value={form.unitOfMeasure}
                  onChange={(event) => setForm({ ...form, unitOfMeasure: event.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Certified Value"
                  type="number"
                  value={form.certifiedValue}
                  onChange={(event) => setForm({ ...form, certifiedValue: event.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Retention %"
                  type="number"
                  value={form.retentionPercent}
                  onChange={(event) => setForm({ ...form, retentionPercent: event.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Notes"
                  value={form.notes}
                  onChange={(event) => setForm({ ...form, notes: event.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateLine}
                  disabled={saving || !form.description || !form.certifiedValue}
                >
                  Add Certificate Line
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                Certificate Lines
              </Typography>
              <TextField
                size="small"
                label="Drawdown Reference"
                value={drawdownReference}
                onChange={(event) => setDrawdownReference(event.target.value)}
              />
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Line</TableCell>
                  <TableCell>WBS</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Gross</TableCell>
                  <TableCell align="right">Retention</TableCell>
                  <TableCell align="right">Net</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {certificateLines.map((line) => (
                  <TableRow key={line.id} hover>
                    <TableCell>{line.lineCode}</TableCell>
                    <TableCell>{line.wbs?.code || line.wbsId || '-'}</TableCell>
                    <TableCell>{line.description}</TableCell>
                    <TableCell align="right">{formatMoney(line.certifiedValue)}</TableCell>
                    <TableCell align="right">{formatMoney(line.retentionAmount)}</TableCell>
                    <TableCell align="right">{formatMoney(line.netCertifiedValue)}</TableCell>
                    <TableCell>{line.status}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          startIcon={<CertifyIcon />}
                          onClick={() => openCertifyDialog(line)}
                          disabled={saving || (line.status === 'certified' && Number(line.percentCertified) >= 100)}
                        >
                          Certify
                        </Button>
                        <Button
                          size="small"
                          startIcon={<RejectIcon />}
                          onClick={() => setRejectDialogLine(line)}
                          disabled={saving || line.status === 'rejected'}
                        >
                          Reject
                        </Button>
                        <Button
                          size="small"
                          startIcon={<PacketIcon />}
                          onClick={() => handleAddToDrawdown(line)}
                          disabled={saving || !drawdownReference}
                        >
                          Packet
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {certificateLines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Alert severity="info">No certificate lines have been entered for this project.</Alert>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                Drawdown Preview
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handlePreviewPacket}
                  disabled={saving}
                >
                  Preview
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DocumentIcon />}
                  onClick={handleCreatePacketDocument}
                  disabled={saving}
                >
                  Link Document
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadPacket}
                  disabled={saving}
                >
                  PDF
                </Button>
              </Stack>
            </Stack>
            {drawdownGroups.length ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Group</TableCell>
                    <TableCell align="right">Items</TableCell>
                    <TableCell align="right">Gross</TableCell>
                    <TableCell align="right">Retention</TableCell>
                    <TableCell align="right">Net</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {drawdownGroups.map((group) => (
                    <>
                      <TableRow key={group.key}>
                        <TableCell>{group.label}</TableCell>
                        <TableCell align="right">{group.itemCount}</TableCell>
                        <TableCell align="right">{formatMoney(group.totals?.grossAmount || 0)}</TableCell>
                        <TableCell align="right">{formatMoney(group.totals?.retentionAmount || 0)}</TableCell>
                        <TableCell align="right">{formatMoney(group.totals?.netAmount || 0)}</TableCell>
                      </TableRow>
                      {(group.items || []).map((item) => (
                        <TableRow key={item.id}>
                          <TableCell colSpan={2} sx={{ pl: 4 }}>
                            <Typography variant="body2">{item.description}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.status}
                            </Typography>
                          </TableCell>
                          <TableCell colSpan={3}>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {readinessFields.map((field) => (
                                <FormControlLabel
                                  key={field.key}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={Boolean(item.readinessChecklist?.[field.key])}
                                      onChange={(event) =>
                                        handleToggleReadiness(
                                          item.id,
                                          item.readinessChecklist,
                                          field.key,
                                          event.target.checked
                                        )
                                      }
                                      disabled={saving}
                                    />
                                  }
                                  label={field.label}
                                />
                              ))}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Alert severity="info">No drawdown items are linked to {drawdownReference} yet.</Alert>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={Boolean(pdfPreviewUrl)}
        onClose={() => {
          if (pdfPreviewUrl) {
            window.URL.revokeObjectURL(pdfPreviewUrl);
          }
          setPdfPreviewUrl(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Drawdown Packet PDF Preview</DialogTitle>
        <DialogContent>
          {pdfPreviewUrl && (
            <Box
              component="iframe"
              src={pdfPreviewUrl}
              title="Drawdown packet PDF preview"
              sx={{ width: '100%', height: '70vh', border: 0 }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(rejectDialogLine)} onClose={() => setRejectDialogLine(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Certificate Line</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {rejectDialogLine?.lineCode} - {rejectDialogLine?.description}
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Reason"
              value={rejectReason}
              onChange={(event) => setRejectReason(event.target.value)}
              multiline
              minRows={3}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={() => setRejectDialogLine(null)} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<RejectIcon />}
                onClick={handleReject}
                disabled={saving || !rejectReason.trim()}
              >
                Reject
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(certifyDialogLine)} onClose={() => setCertifyDialogLine(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Certify Certificate Line</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {certifyDialogLine?.lineCode} — {certifyDialogLine?.description}
            </Typography>
            {certifyDialogLine && Number(certifyDialogLine.percentCertified) > 0 && (
              <Alert severity="info">
                Currently certified at <strong>{Number(certifyDialogLine.percentCertified)}%</strong>.
                Choose a higher percentage to top up cumulative certification.
              </Alert>
            )}
            <TextField
              fullWidth
              size="small"
              label="Cumulative percent certified"
              type="number"
              value={certifyPercent}
              onChange={(event) => setCertifyPercent(Number(event.target.value))}
              inputProps={{ min: 1, max: 100, step: 1 }}
              helperText="1–100, steps by 1%. Backend stores partial value + retention proportional to this percentage."
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={() => setCertifyDialogLine(null)} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<CertifyIcon />}
                onClick={handleCertify}
                disabled={
                  saving ||
                  !Number.isFinite(certifyPercent) ||
                  certifyPercent <= 0 ||
                  certifyPercent > 100 ||
                  (certifyDialogLine ? certifyPercent <= Number(certifyDialogLine.percentCertified || 0) && Number(certifyDialogLine.percentCertified || 0) >= 100 : false)
                }
              >
                Certify {Math.round(certifyPercent)}%
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(claimableDetail)} onClose={() => setClaimableDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>Claimable BQ Detail</DialogTitle>
        <DialogContent>
          {claimableDetail && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Box>
                <Typography variant="h6">{claimableDetail.bqCode}</Typography>
                <Typography variant="body2" color="text.secondary">{claimableDetail.description}</Typography>
              </Box>
              {claimableDetail.readiness?.status === 'blocked' && (
                <Alert severity="error">{claimableDetail.readiness?.blockingReason}</Alert>
              )}
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}><KPICard title="Progress" value={`${claimableDetail.weightedProgressPercent}%`} color="primary" /></Grid>
                <Grid item xs={6} md={3}><KPICard title="This Cert" value={formatMoney(claimableDetail.thisCertificateValue)} color="success" /></Grid>
                <Grid item xs={6} md={3}><KPICard title="Materials" value={formatMoney(claimableDetail.materialsOnSiteValue)} color="info" /></Grid>
                <Grid item xs={6} md={3}><KPICard title="To Complete" value={formatMoney(claimableDetail.toCompleteValue)} color="warning" /></Grid>
              </Grid>
              <Typography variant="subtitle2">Mapped tasks</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Task</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Weight</TableCell>
                    <TableCell align="right">Progress</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(claimableDetail.mappedTasks || []).map((task) => (
                    <TableRow key={task.id}>
                      <TableCell>{task.taskCode} - {task.name}</TableCell>
                      <TableCell>{task.lifecycleStatus}</TableCell>
                      <TableCell align="right">{task.weightPercent}%</TableCell>
                      <TableCell align="right">{task.completionPercentage}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="subtitle2">Materials on site</Typography>
              {(claimableDetail.materials || []).length ? (
                <Table size="small">
                  <TableBody>
                    {(claimableDetail.materials || []).map((material) => (
                      <TableRow key={material.id}>
                        <TableCell>{material.description || material.source || 'Material allocation'}</TableCell>
                        <TableCell>{material.status}</TableCell>
                        <TableCell align="right">{formatMoney(material.claimableValue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Alert severity="info">No claimable materials-on-site allocation is linked to this line.</Alert>
              )}
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={() => setClaimableDetail(null)}>Close</Button>
                <Button
                  startIcon={<RejectIcon />}
                  onClick={() => handleRejectClaimable(claimableDetail)}
                  disabled={saving}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CertifyIcon />}
                  onClick={() => handleCertifyClaimable(claimableDetail)}
                  disabled={saving || claimableDetail.readiness?.status !== 'ready' || claimableDetail.thisCertificateValue <= 0}
                >
                  Certify {formatMoney(claimableDetail.thisCertificateValue)}
                </Button>
              </Stack>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
